import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

type PostHogInstance = ReturnType<typeof usePostHog>;

function reportNavTiming(posthog: PostHogInstance): void {
  if (!posthog) return;
  const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  if (!nav) return;

  posthog.capture('app_performance_timing', {
    dns_ms: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
    tcp_ms: Math.round(nav.connectEnd - nav.connectStart),
    tls_ms:
      nav.secureConnectionStart > 0 ? Math.round(nav.connectEnd - nav.secureConnectionStart) : 0,
    ttfb_ms: Math.round(nav.responseStart - nav.fetchStart),
    download_ms: Math.round(nav.responseEnd - nav.responseStart),
    dom_interactive_ms: Math.round(nav.domInteractive - nav.startTime),
    dom_content_loaded_ms: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
    load_ms: Math.round(nav.loadEventEnd - nav.startTime),
    page_path: window.location.pathname,
  });
}

// Measures TTI using the Long Tasks API: TTI = start of the first 5s quiet
// window (no long task > 50ms) that begins at or after FCP.
// Falls back to domInteractive on browsers without longtask support.
function observeTTI(posthog: PostHogInstance): () => void {
  if (!posthog) return () => {};

  const longTaskSupported =
    typeof PerformanceObserver !== 'undefined' &&
    Array.isArray(PerformanceObserver.supportedEntryTypes) &&
    PerformanceObserver.supportedEntryTypes.includes('longtask');

  if (!longTaskSupported) {
    const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (nav?.domInteractive > 0) {
      posthog.capture('app_tti', {
        tti_ms: Math.round(nav.domInteractive),
        method: 'dom_interactive',
      });
    }
    return () => {};
  }

  let lastLongTaskEnd = 0;
  let fcpTime = 0;
  let reported = false;

  const fcpObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntriesByName('first-contentful-paint')) {
      fcpTime = entry.startTime;
      fcpObserver.disconnect();
    }
  });
  fcpObserver.observe({ type: 'paint', buffered: true });

  const longTaskObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      lastLongTaskEnd = entry.startTime + entry.duration;
    }
  });
  longTaskObserver.observe({ type: 'longtask', buffered: true });

  let timer: ReturnType<typeof setTimeout>;

  const check = () => {
    if (reported) return;
    if (fcpTime === 0) {
      timer = setTimeout(check, 1000);
      return;
    }
    const now = performance.now();
    const quietFrom = Math.max(fcpTime, lastLongTaskEnd);
    if (now - quietFrom >= 5000) {
      reported = true;
      posthog.capture('app_tti', { tti_ms: Math.round(quietFrom), method: 'long_task' });
      fcpObserver.disconnect();
      longTaskObserver.disconnect();
    } else {
      timer = setTimeout(check, 1000);
    }
  };

  timer = setTimeout(check, 6000);

  return () => {
    clearTimeout(timer);
    fcpObserver.disconnect();
    longTaskObserver.disconnect();
  };
}

/**
 * Fires two custom PostHog events per page load:
 *   - app_performance_timing: detailed Navigation Timing breakdown (dns_ms, tcp_ms, tls_ms,
 *     ttfb_ms, download_ms, dom_interactive_ms, dom_content_loaded_ms, load_ms, page_path)
 *   - app_tti: Time to Interactive in ms, measured via Long Tasks API (method: 'long_task')
 *     or Navigation Timing domInteractive (method: 'dom_interactive') as fallback
 *
 * Complements PostHog's built-in $web_vitals (LCP/FCP/CLS/INP/TTFB) which is captured
 * automatically via capture_performance: { web_vitals: true }.
 */
export function PerformanceTracker() {
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog || typeof window === 'undefined' || !window.performance) return;

    const onLoad = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => reportNavTiming(posthog), { timeout: 5000 });
      } else {
        setTimeout(() => reportNavTiming(posthog), 0);
      }
    };

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }

    const cleanupTTI = observeTTI(posthog);

    return () => {
      window.removeEventListener('load', onLoad);
      cleanupTTI();
    };
  }, [posthog]);

  return null;
}
